<section class="space-y-6" role="region" aria-label="Brands">
  <!-- Hero Section -->
  <div
    class="hero overflow-hidden rounded-3xl border border-base-200 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/10 shadow-md"
    role="banner"
  >
    <div class="hero-content w-full max-w-none flex-col items-stretch gap-6 p-4 sm:p-6 lg:p-8">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div class="max-w-2xl space-y-3">
          <div class="badge badge-primary badge-outline gap-2 px-3 py-3 text-xs font-semibold shadow-sm">
            <span class="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"></span>
            Catalog management
          </div>
          <h1 class="text-3xl font-black tracking-tight sm:text-4xl text-base-content">Brand Management</h1>
          <p class="text-base font-medium text-base-content/70">
            Organize and manage your brand catalog, types, and featured statuses.
          </p>
          <div class="flex flex-wrap gap-3">
            <button class="btn btn-primary shadow-lg shadow-primary/20 gap-2" type="button" (click)="openCreate()">
              <i class="fa-solid fa-plus-circle"></i>
              Add New Brand
            </button>
            <button class="btn btn-ghost border-base-300 gap-2" type="button" (click)="loadBrands()" [disabled]="loading()">
              <i class="fa-solid fa-sync-alt" [class.animate-spin]="loading()"></i>
              Refresh
            </button>
          </div>
        </div>

        <!-- Quick Stats / Search -->
        <div class="card bg-base-100 border border-base-200 shadow-sm grow lg:max-w-md">
          <div class="p-4 space-y-4">
            <label class="input input-bordered flex items-center gap-3 w-full bg-base-200/30">
              <i class="fa-solid fa-search opacity-40"></i>
              <input
                type="text"
                class="grow text-sm"
                placeholder="Search brands by name or slug..."
                [ngModel]="searchText()"
                (ngModelChange)="searchText.set($event); resetPageAndLoad()"
              />
            </label>
            <div class="flex items-center justify-between text-xs font-bold text-base-content/50 uppercase tracking-wider px-1">
              <span>Total Brands: {{ brands().length }}</span>
              <span>Sorted by: {{ sortBy() | titlecase }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters & Sorting -->
  <div class="card bg-base-100 border border-base-200 shadow-sm overflow-visible">
    <div class="p-4 flex flex-wrap items-center gap-4">
      <div class="flex items-center gap-2 grow sm:grow-0">
        <span class="text-xs font-bold uppercase opacity-50 px-1 hidden sm:inline">Type:</span>
        <select class="select select-sm select-bordered grow sm:w-40" [ngModel]="filterType()" (ngModelChange)="filterType.set($event); resetPageAndLoad()">
          <option value="">All Types</option>
          <option value="national">National</option>
          <option value="regional">Regional</option>
          <option value="local">Local</option>
          <option value="private_label">Private Label</option>
        </select>
      </div>

      <div class="flex items-center gap-2 grow sm:grow-0">
        <span class="text-xs font-bold uppercase opacity-50 px-1 hidden sm:inline">Status:</span>
        <select class="select select-sm select-bordered grow sm:w-36" [ngModel]="filterStatus()" (ngModelChange)="filterStatus.set($event); resetPageAndLoad()">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div class="divider divider-horizontal mx-0 hidden md:flex"></div>

      <label class="label cursor-pointer gap-3 bg-base-200/50 px-3 py-1.5 rounded-lg hover:bg-base-200 transition-colors">
        <input type="checkbox" class="checkbox checkbox-primary checkbox-sm" [ngModel]="filterFeaturedOnly()" (ngModelChange)="filterFeaturedOnly.set($event); resetPageAndLoad()" />
        <span class="label-text font-bold text-xs">Featured Only</span>
      </label>

      <div class="flex items-center gap-2 ml-auto">
        <span class="text-xs font-bold uppercase opacity-50 px-1">Sort:</span>
        <div class="join border border-base-300">
          <select class="select select-sm join-item bg-base-100 border-0 focus:ring-0" [ngModel]="sortBy()" (ngModelChange)="sortBy.set($event); resetPageAndLoad()">
            <option value="created_at">Latest</option>
            <option value="name">Alphabetical</option>
          </select>
          <button class="btn btn-sm join-item border-0 border-l border-base-300 bg-base-100 hover:bg-base-200" 
            (click)="sortOrder.set(sortOrder() === 'ASC' ? 'DESC' : 'ASC'); resetPageAndLoad()">
            <i class="fa-solid" [class.fa-sort-amount-down]="sortOrder() === 'DESC'" [class.fa-sort-amount-up]="sortOrder() === 'ASC'"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Content -->
  @if (loading() && brands().length === 0) {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (i of [1,2,3,4,5,6]; track i) {
        <div class="card bg-base-100 border border-base-200 p-6 space-y-4">
          <div class="flex items-center gap-4">
            <div class="skeleton w-16 h-16 rounded-2xl"></div>
            <div class="space-y-2 grow">
              <div class="skeleton h-4 w-3/4"></div>
              <div class="skeleton h-3 w-1/2"></div>
            </div>
          </div>
          <div class="skeleton h-20 w-full"></div>
        </div>
      }
    </div>
  } @else if (error()) {
    <div class="alert alert-error shadow-lg max-w-lg mx-auto mt-10">
      <i class="fa-solid fa-circle-exclamation text-xl"></i>
      <div>
        <h4 class="font-bold">Fetch Failed</h4>
        <p class="text-sm opacity-80">{{ error() }}</p>
      </div>
      <button class="btn btn-sm btn-ghost" type="button" (click)="loadBrands()">Retry</button>
    </div>
  } @else {
    <div class="card bg-base-100 border border-base-200 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="table table-zebra">
          <thead class="bg-base-200/50">
            <tr class="text-base-content/50 uppercase text-[10px] tracking-widest font-black">
              <th class="pl-6">Brand Info</th>
              <th>Type</th>
              <th>Status</th>
              <th>Featured</th>
              <th>Products</th>
              <th>Created</th>
              <th class="pr-6 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-sm">
            @for (brand of paginatedBrands(); track brand.id) {
              <tr class="hover:bg-base-200/30 transition-colors group">
                <td class="pl-6 py-4">
                  <div class="flex items-center gap-4">
                    <div class="avatar">
                      <div class="w-14 h-14 rounded-2xl border border-base-300 bg-base-100 shadow-sm p-1">
                        @if (brand.icon?.url) {
                          <img [src]="brand.icon?.url" [alt]="brand.name" class="object-contain" />
                        } @else {
                          <div class="w-full h-full flex items-center justify-center bg-base-200 text-base-content/20">
                            <i class="fa-solid fa-image text-xl"></i>
                          </div>
                        }
                      </div>
                    </div>
                    <div class="min-w-0">
                      <div class="font-black text-base truncate">{{ brand.name }}</div>
                      <div class="text-[10px] font-bold opacity-40 uppercase tracking-tighter">{{ brand.slug }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge badge-sm font-bold border-base-300 capitalize text-[10px]">
                    {{ brand.brand_type?.replace('_', ' ') }}
                  </span>
                </td>
                <td>
                  <div class="dropdown dropdown-end">
                    <button tabindex="0" class="badge badge-sm font-black text-[10px] cursor-pointer hover:scale-105 active:scale-95 transition-all"
                      [class.badge-success]="brand.status === 'published'"
                      [class.badge-warning]="brand.status === 'draft'"
                      [class.badge-error]="brand.status === 'archived'">
                      {{ brand.status | uppercase }}
                    </button>
                    <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow-xl bg-base-100 rounded-box w-40 border border-base-200 mt-2">
                       <li class="menu-title text-[9px] uppercase tracking-widest">Change Status</li>
                       <li><button type="button" (click)="quickStatusChange(brand, 'draft')" class="text-xs font-bold">Draft</button></li>
                       <li><button type="button" (click)="quickStatusChange(brand, 'published')" class="text-xs font-bold text-success">Publish</button></li>
                       <li><button type="button" (click)="quickStatusChange(brand, 'archived')" class="text-xs font-bold text-error">Archive</button></li>
                    </ul>
                  </div>
                </td>
                <td>
                  @if (brand.is_featured) {
                    <div class="tooltip tooltip-right" data-tip="Featured Brand">
                      <i class="fa-solid fa-star text-warning animate-pulse"></i>
                    </div>
                  } @else {
                    <span class="opacity-10">-</span>
                  }
                </td>
                <td>
                  <div class="badge badge-ghost font-black text-xs">{{ brand.product_count }}</div>
                </td>
                <td>
                  <div class="text-[10px] font-bold opacity-60">{{ brand.createdAt | date:'shortDate' }}</div>
                </td>
                <td class="pr-6 text-right">
                  <div class="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button class="btn btn-ghost btn-sm btn-square hover:bg-primary/20 hover:text-primary transition-all" (click)="openEdit(brand)">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="btn btn-ghost btn-sm btn-square hover:bg-error/20 hover:text-error transition-all" (click)="requestDelete(brand)">
                      <i class="fa-solid fa-trash-alt text-xs"></i>
                    </button>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="7">
                  <div class="py-20 flex flex-col items-center justify-center text-base-content/30 italic">
                    <i class="fa-solid fa-inbox text-5xl mb-4 opacity-10"></i>
                    <p class="text-lg font-bold">No brands matching your search</p>
                    <button class="btn btn-link btn-xs mt-2" (click)="searchText.set(''); filterType.set(''); loadBrands()">Clear all filters</button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      @if (filteredBrands().length > 0) {
        <div class="flex flex-col gap-3 border-t border-base-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div class="flex flex-wrap items-center gap-3 text-xs font-bold text-base-content/60">
            <span>
              Showing {{ firstVisibleBrand() }}-{{ lastVisibleBrand() }} of {{ filteredBrands().length }} brands
            </span>
            <label class="flex items-center gap-2">
              <span>Rows</span>
              <select
                class="select select-xs select-bordered"
                [ngModel]="pageSize()"
                (ngModelChange)="setPageSize($event)"
                aria-label="Brands per page"
              >
                <option [ngValue]="10">10</option>
                <option [ngValue]="25">25</option>
                <option [ngValue]="50">50</option>
              </select>
            </label>
          </div>

          <div class="join self-end sm:self-auto" aria-label="Brand list pagination">
            <button
              type="button"
              class="btn btn-sm join-item"
              (click)="setPage(currentPage() - 1)"
              [disabled]="currentPage() === 1"
              aria-label="Previous page"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            @for (page of pageNumbers(); track page) {
              <button
                type="button"
                class="btn btn-sm join-item"
                [class.btn-primary]="currentPage() === page"
                [class.btn-ghost]="currentPage() !== page"
                (click)="setPage(page)"
                [attr.aria-current]="currentPage() === page ? 'page' : null"
              >
                {{ page }}
              </button>
            }
            <button
              type="button"
              class="btn btn-sm join-item"
              (click)="setPage(currentPage() + 1)"
              [disabled]="currentPage() === totalPages()"
              aria-label="Next page"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      }
    </div>
  }

  <!-- Form Modal -->
  @if (draft(); as d) {
    <div class="modal modal-open bg-base-300/80 backdrop-blur-sm" role="dialog">
      <div class="modal-box max-w-2xl p-0 overflow-hidden rounded-3xl border border-base-200">
        <!-- Modal Header -->
        <div class="bg-gradient-to-r from-primary to-primary-focus p-6 text-primary-content">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md">
                <i class="fa-solid {{ d.mode === 'create' ? 'fa-plus' : 'fa-edit' }} text-xl"></i>
              </div>
              <div>
                <h3 class="text-xl font-black">{{ d.mode === 'create' ? 'Create New Brand' : 'Update Brand' }}</h3>
                <p class="text-xs opacity-70 font-bold uppercase tracking-widest mt-0.5">Brand Configuration</p>
              </div>
            </div>
            <button class="btn btn-sm btn-circle btn-ghost" (click)="closeDraft()" [disabled]="saving()">✕</button>
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="p-8 max-h-[70vh] overflow-y-auto space-y-8 custom-scrollbar">
          <!-- Icon & Basic Info -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div class="space-y-4 flex flex-col items-center">
              <div class="label-text font-black uppercase text-[10px] tracking-widest opacity-50 w-full">Brand Identity</div>
              
              @if (d.icon_preview_url) {
                <div class="relative w-28 h-28 rounded-3xl border-2 border-base-200 overflow-hidden bg-base-100 shadow-md flex items-center justify-center">
                  <img [src]="d.icon_preview_url" class="absolute inset-0 w-full h-full object-contain p-2" alt="Brand icon preview" />
                  <button class="absolute top-1 right-1 btn btn-xs btn-circle btn-error opacity-80 hover:opacity-100" (click)="removeIcon()" title="Remove Icon">✕</button>
                </div>
              }
              @if (!d.icon_preview_url) {
                <app-image-uploader 
                  type="BRAND_LOGO" 
                  [multiple]="false" 
                  (filesSelected)="onFilesValidResult($event)">
                </app-image-uploader>
              }

              <p class="text-[9px] text-center opacity-40 leading-relaxed font-medium">Recommended: 400x400 PNG/WebP</p>
            </div>

            <div class="md:col-span-2 space-y-4">
              <div class="form-control w-full">
                <div class="label"><span class="label-text font-black uppercase text-[10px] tracking-widest opacity-50">Brand Name *</span></div>
                <input type="text" class="input input-bordered focus:input-primary font-bold" placeholder="e.g. Apple, Nike, Nestle" 
                  [ngModel]="d.name" (ngModelChange)="onNameChange($event)" />
              </div>

              <div class="form-control w-full">
                <div class="label">
                  <span class="label-text font-black uppercase text-[10px] tracking-widest opacity-50">Slug Handle</span>
                </div>
                <div class="join w-full">
                  <div class="join-item bg-base-200 px-3 flex items-center text-[10px] font-black border border-base-300">/brand/</div>
                  <input type="text" class="input input-bordered join-item grow font-mono text-xs focus:input-primary" 
                    [ngModel]="d.slug" (ngModelChange)="d.slug = $event" />
                </div>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <!-- Attributes -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="form-control">
              <div class="label"><span class="label-text font-black uppercase text-[10px] tracking-widest opacity-50">Operations Type</span></div>
              <select class="select select-bordered font-bold" [(ngModel)]="d.brand_type">
                <option value="national">National Brand</option>
                <option value="regional">Regional Brand</option>
                <option value="local">Local Brand</option>
                <option value="private_label">Private Label</option>
              </select>
            </div>

            <div class="form-control">
              <div class="label"><span class="label-text font-black uppercase text-[10px] tracking-widest opacity-50">Visibility Status</span></div>
              <select class="select select-bordered font-bold text-success" [(ngModel)]="d.status">
                <option value="draft" class="text-warning">Draft (Internal)</option>
                <option value="published" class="text-success">Published (Live)</option>
                <option value="archived" class="text-error">Archived (Hidden)</option>
              </select>
            </div>
          </div>

          <div class="form-control">
            <div class="label"><span class="label-text font-black uppercase text-[10px] tracking-widest opacity-50">About the Brand</span></div>
            <textarea class="textarea textarea-bordered h-24 font-medium" placeholder="Write a short biography or description for consumers..."
              [(ngModel)]="d.description"></textarea>
          </div>

          <div class="bg-base-200/50 rounded-2xl p-4 flex items-center justify-between border border-base-300">
            <div>
              <span class="block font-black text-sm uppercase tracking-tight">Promote to Featured</span>
              <span class="text-[10px] opacity-50 font-bold">Showcase this brand in home screen collections</span>
            </div>
            <input type="checkbox" class="toggle toggle-primary toggle-lg" [(ngModel)]="d.is_featured" />
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="bg-base-200 p-6 flex justify-end gap-3 rounded-b-3xl">
          <button class="btn btn-ghost font-black" (click)="closeDraft()" [disabled]="saving()">Dismiss</button>
          <button class="btn btn-primary px-10 shadow-lg shadow-primary/20 font-black gap-2" (click)="saveDraft()" [disabled]="saving()">
            @if (saving()) { <span class="loading loading-spinner loading-sm"></span> }
            {{ d.mode === 'create' ? 'Confirm Brand' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Delete Confirmation -->
  @if (deleteDraft(); as d) {
    <div class="modal modal-open animate-in fade-in zoom-in duration-200" role="dialog">
      <div class="modal-box border border-error/20 rounded-3xl p-0 overflow-hidden shadow-2xl">
        <div class="bg-error/10 p-6 flex flex-col items-center text-center space-y-3">
          <div class="w-16 h-16 rounded-full bg-error/20 flex items-center justify-center">
            <i class="fa-solid fa-trash-alt text-error text-2xl"></i>
          </div>
          <div>
            <h3 class="text-xl font-black text-error">Archive Brand?</h3>
            <p class="text-xs font-bold opacity-60 mt-1 max-w-xs uppercase tracking-widest">
              Are you sure you want to remove <span class="text-base-content">{{ d.name }}</span> from active catalog?
            </p>
          </div>
        </div>
        <div class="p-6 bg-base-100 flex gap-2">
          <button class="btn btn-ghost grow font-black" (click)="closeDelete()" [disabled]="deleting()">Go Back</button>
          <button class="btn btn-error grow font-black shadow-lg shadow-error/20" (click)="confirmDelete()" [disabled]="deleting()">
            @if (deleting()) { <span class="loading loading-spinner loading-sm"></span> }
            Yes, Archive
          </button>
        </div>
      </div>
      <button class="modal-backdrop bg-base-300/80 backdrop-blur-sm" (click)="closeDelete()" [disabled]="deleting()">Close</button>
    </div>
  }
</section>

